import Reveal from "@Components/Reveal/Reveal";
import AmbassadorPanel from "./AmbassadorPanel";
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import styles from "./AmbassadorBenefits.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images and animations come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("AmbassadorV2");

const lottieEarn = asset("benefits-earn.json");
const lottieJobs = asset("benefits-jobs.json");
const lottieBrand = asset("benefits-personalbrand.json");
const lottieUpskill = asset("benefits-upskill.json");
const lottieHustle = asset("benefits-hustle.json");
const lottieNetwork = asset("benefits-network.json");

/**
 * "Why join" — the six things the programme pays out in.
 *
 * ── Three columns with the image between them ──────────────────────────────
 * Six items split three-and-three either side of one square image. Each item is
 * an animated mark over a single paragraph that opens with its own bold lead-in,
 * with hairlines between items rather than borders around them.
 *
 * ⚠️  WHY NOT CARDS. This was a 3x2 card grid, and the copy defeated it: every
 * benefit is a one-liner ("Earn — monetary and exclusive goodies"). Six
 * one-liners in six bordered boxes leaves each box mostly empty and reads as a
 * section with its copy missing. As six rules-separated entries in two columns
 * the same words read as a list of terms, and the image carries the visual
 * weight the copy cannot.
 *
 * ⚠️  ONE MARK IS A COMPROMISE. The icon library has no briefcase and no
 * graduation cap outside its AI-branded set, so "jobs" carries a folder — the
 * nearest honest match for a portfolio of work.
 */
/**
 * ⚠️  NEW COPY, DRAFTED HERE — NOT THE OLD KEYS, AND NOT SIGNED OFF.
 *
 * The `ambassador.benefits.*.subtitle` keys are five-word fragments ("Monetary
 * and exclusive goodies"). Six of those beside a 520px image left the band with
 * nothing in it to read. These run to about two lines each at the 44ch measure —
 * enough to say what the benefit actually is, and no more.
 *
 * The substance comes from things the programme demonstrably does, most of it
 * from the three ambassadors' own accounts in the section below: paid property
 * tours and reviews, PR and marketing practice, briefs from the amber team, and
 * meeting students across campuses. Nothing here claims a pipeline or an audience
 * the programme has not actually got.
 *
 * STILL TO DO: someone who owns the programme should check these read true, and
 * they need `ambassador.v2.benefits.*` keys in translations.json before the page
 * ships to a localised route. The titles still come from the existing keys.
 */
// The one-line descriptions under each benefit, from `ambassador.benefits.<id>.body`.
//
// ⚠️  THESE WERE ENGLISH LITERALS, so every localised route (/de/ambassador, …) showed translated
// titles over English sentences. They are keys now, in all ten locales. The older
// `ambassador.benefits.<id>.subtitle` keys hold the previous, shorter copy and are left alone.

const LEFT = [
  { id: "earn", mark: lottieEarn },
  { id: "jobs", mark: lottieJobs },
  { id: "personalbrand", mark: lottieBrand },
];

const RIGHT = [
  { id: "upskill", mark: lottieUpskill },
  { id: "hustle", mark: lottieHustle },
  { id: "network", mark: lottieNetwork },
];

type Item = { id: string; mark: string };

/**
 * One column of benefits.
 *
 * ⚠️  THE TWO COLUMNS SHARE ONE GRID BELOW `lg`. Laid out as two independent lists, each
 * column's hairlines sat wherever its own text happened to end, so at two columns they drifted
 * apart — worst in the longer locales (German) — and stacked, the right column's first item
 * was a `:first-child` with no rule over it, leaving the seam between the lists unmarked. Below
 * `lg` the lists are `display: contents`, and each item is placed on the parent grid by the two
 * row numbers the stylesheet gives each position: `--row2` puts paired items side by side on one row (so their rules line
 * up whatever the copy), `--row1` runs all six in order in the single column.
 */
const Column = ({
  items,
  offset,
  side,
}: {
  items: Item[];
  offset: number;
  side: "left" | "right";
}) => {
  const { t } = useTranslationHook();

  return (
    <ul className={`${styles.col} ${side === "left" ? styles.colLeft : styles.colRight}`}>
      {items.map((benefit, i) => (
        <Reveal as="li" className={styles.item} key={benefit.id} delay={(offset + i) * 70}>
          <InlineLottie
            className={styles.mark}
            src={benefit.mark}
            size={30}
            delay={(offset + i) * 70}
          />
          {/* Mark, then title, then description — three stacked elements. The
              title is a real heading rather than a bold lead-in running into the
              sentence, so it carries no trailing full stop. */}
          <h3 className={styles.itemTitle}>{t(`ambassador.benefits.${benefit.id}.title`)}</h3>
          <p className={styles.itemBody}>{t(`ambassador.benefits.${benefit.id}.body`)}</p>
        </Reveal>
      ))}
    </ul>
  );
};

const Benefits = () => {
  const { t } = useTranslationHook();

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* `inview`, not the default `early`: a section heading is scrolled to
            deliberately, and the default trigger fires a fifth of a screen below
            the fold — so its 0.7s entrance was over before the heading arrived
            and read as no animation at all. The cards and list items below keep
            the default, where a reader should never wait on a transition. */}
        <Reveal className={styles.head} trigger="inview">
          <h2 className={styles.heading}>{t("ambassador.whyjoin.title")}</h2>
          <p className={styles.lede}>{t("ambassador.whyjoin.subtitle")}</p>
        </Reveal>

        <div className={styles.split}>
          <Column items={LEFT} offset={0} side="left" />

          {/* Sits between the columns on a wide screen and above them below lg,
              where a tall plate squeezed into a third of the width stops earning
              its space. Stretched to the full height of the benefit columns —
              see `.figure`. */}
          <div className={styles.figure}>
            <AmbassadorPanel />
          </div>

          <Column items={RIGHT} offset={3} side="right" />
        </div>
      </div>
    </section>
  );
};

export default wrapperHOC(Benefits, {
  componentName: "Benefits-AmbassadorV2",
  showForChina: true,
});
